import { useState, useEffect } from 'react';
import type { Theme } from '../lib/tokens';
import { FONTS } from '../lib/tokens';
import { Icon } from '../components/icons';
import { useApi } from '../hooks/useAuth';
import { hapticLight } from '../lib/haptics';

const REASONS: { key: string; label: string }[] = [
  { key: 'HARASSMENT', label: 'Harassment or threats' },
  { key: 'INAPPROPRIATE', label: 'Inappropriate content' },
  { key: 'FAKE_PROFILE', label: 'Fake profile' },
  { key: 'NO_SHOW', label: "Didn't show up to a date" },
  { key: 'OTHER', label: 'Something else' },
];

interface ReportBlockSheetProps {
  targetUserId: string;
  targetName: string;
  t: Theme;
  onClose: () => void;
  // Called after a successful report or block, so the caller can e.g. leave the
  // chat / close the profile since the person is now hidden.
  onDone?: () => void;
}

export function ReportBlockSheet({ targetUserId, targetName, t, onClose, onDone }: ReportBlockSheetProps) {
  const { apiFetch } = useApi();
  const [show, setShow] = useState(false);
  const [reason, setReason] = useState<string | null>(null);
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<'reported' | 'blocked' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { requestAnimationFrame(() => setShow(true)); }, []);

  const close = () => { setShow(false); setTimeout(onClose, 220); };

  const submitReport = async () => {
    if (!reason || busy) return;
    setBusy(true); setError(null);
    try {
      await apiFetch('/api/safety/report', {
        method: 'POST',
        body: JSON.stringify({ userId: targetUserId, reason, details: details.trim() || undefined }),
      });
      setDone('reported');
      setTimeout(() => { onDone?.(); close(); }, 1600);
    } catch (e: any) {
      setError(e.message || 'Could not submit. Please try again.');
      setBusy(false);
    }
  };

  const submitBlock = async () => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      await apiFetch('/api/safety/block', {
        method: 'POST',
        body: JSON.stringify({ userId: targetUserId }),
      });
      setDone('blocked');
      setTimeout(() => { onDone?.(); close(); }, 1400);
    } catch (e: any) {
      setError(e.message || 'Could not block. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div
      onClick={close}
      style={{
        position: 'fixed', inset: 0, zIndex: 400,
        background: show ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0)',
        transition: 'background 220ms', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 430, margin: '0 auto', boxSizing: 'border-box',
          background: t.paper, borderRadius: '24px 24px 0 0',
          padding: '10px 20px max(20px, env(safe-area-inset-bottom))',
          transform: show ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 260ms cubic-bezier(0.22, 1, 0.36, 1)',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.18)',
        }}
      >
        {/* Grab handle */}
        <div style={{ width: 40, height: 4, borderRadius: 100, background: t.line, margin: '0 auto 14px' }} />

        {done ? (
          <div style={{ textAlign: 'center', padding: '20px 8px 28px' }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: t.sage, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              {Icon.check ? Icon.check({ s: 24, c: t.paper }) : <span style={{ color: t.paper, fontSize: 24 }}>✓</span>}
            </div>
            <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 22, color: t.ink, marginBottom: 8 }}>
              {done === 'reported' ? 'Report sent' : `${targetName} blocked`}
            </div>
            <div style={{ fontFamily: FONTS.sans, fontSize: 13.5, color: t.inkMute, lineHeight: 1.5, maxWidth: 280, margin: '0 auto' }}>
              {done === 'reported'
                ? "We've hidden them from you and our team will review it."
                : "You won't see each other anywhere on Preminade."}
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <h2 style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontWeight: 400, fontSize: 24, color: t.ink, margin: 0 }}>
                Report {targetName}
              </h2>
              <button onClick={close} style={{ all: 'unset', cursor: 'pointer', width: 32, height: 32, borderRadius: '50%', background: t.paperAlt, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {Icon.x({ s: 16, c: t.ink })}
              </button>
            </div>
            <p style={{ fontFamily: FONTS.sans, fontSize: 13, color: t.inkMute, margin: '0 0 16px', lineHeight: 1.5 }}>
              Reporting also blocks them — you won't see each other again. Reports are confidential.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
              {REASONS.map(r => {
                const active = reason === r.key;
                return (
                  <button
                    key={r.key}
                    onClick={() => { hapticLight(); setReason(r.key); }}
                    style={{
                      all: 'unset', cursor: 'pointer', boxSizing: 'border-box', width: '100%',
                      padding: '13px 16px', borderRadius: 14,
                      background: active ? t.terracotta : t.card,
                      border: `1.5px solid ${active ? t.terracotta : t.line}`,
                      color: active ? t.paper : t.ink,
                      fontFamily: FONTS.sans, fontSize: 14, fontWeight: active ? 600 : 500,
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}
                  >
                    {r.label}
                    {active && <span style={{ fontSize: 14 }}>✓</span>}
                  </button>
                );
              })}
            </div>

            {reason && (
              <textarea
                value={details}
                onChange={e => setDetails(e.target.value)}
                placeholder="Add anything that helps us understand (optional)"
                rows={3}
                maxLength={2000}
                style={{
                  width: '100%', boxSizing: 'border-box', resize: 'none',
                  border: `1.5px solid ${t.line}`, borderRadius: 14, padding: '12px 14px',
                  fontFamily: FONTS.sans, fontSize: 14, color: t.ink, background: t.card,
                  outline: 'none', lineHeight: 1.5, marginBottom: 14,
                }}
              />
            )}

            {error && (
              <div style={{ fontFamily: FONTS.sans, fontSize: 12.5, color: t.terracotta, marginBottom: 12, textAlign: 'center' }}>
                {error}
              </div>
            )}

            <button
              onClick={submitReport}
              disabled={!reason || busy}
              style={{
                all: 'unset', cursor: reason && !busy ? 'pointer' : 'default', boxSizing: 'border-box', width: '100%',
                textAlign: 'center', padding: '15px 0', borderRadius: 16,
                background: reason && !busy ? t.terracotta : t.line,
                color: reason ? t.paper : t.inkMute,
                fontFamily: FONTS.sans, fontSize: 15, fontWeight: 600, marginBottom: 8,
                transition: 'background 150ms',
              }}
            >
              {busy ? 'Sending…' : 'Report & block'}
            </button>

            <button
              onClick={submitBlock}
              disabled={busy}
              style={{
                all: 'unset', cursor: busy ? 'default' : 'pointer', boxSizing: 'border-box', width: '100%',
                textAlign: 'center', padding: '11px 0',
                fontFamily: FONTS.sans, fontSize: 13.5, fontWeight: 500, color: t.inkMute,
              }}
            >
              Just block, don't report
            </button>
          </>
        )}
      </div>
    </div>
  );
}
